import { Close as CloseIcon } from "@mui/icons-material";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
} from "@mui/material";
import InputField from "components/Input/InputField";
import UploadFile from "components/Input/UploadFile";
import { MAX_UPLOAD_FILE_SIZE_LABEL } from "constants/fileUploadLimits";
import { useState } from "react";
import { FaDownload, FaSync, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";

const ImportModal = ({
  open,
  handleClose,
  onImportAll,
  onImportSelected,
  onImportUpdated,
  isLoading = false,
}) => {
  const [selectedOption, setSelectedOption] = useState("import-selected");
  const [uploadData, setUploadData] = useState({});
  const [isUploading, setIsUploading] = useState(false);
  const [url, setUrl] = useState(
    "https://docs.google.com/spreadsheets/d/e/2PACX-1vSRdcg10KGkr4GlYMzfVBvCT6C6tftxgTvnvjQztoi8EQxCseiIEfvElVQfBlZ6umsHYq37Q13NIeDh/pub?output=csv"
  );

  const options = [
    {
      id: "import-selected",
      title: "Import Updated Data",
      description: "Import only the updated/new client data",
      icon: <FaSync className="text-green-500" />,
      color: "bg-green-50 hover:bg-green-100 border-green-200",
      selectedColor: "bg-green-100 border-green-400",
    },
    {
      id: "import-all",
      title: "Import All Data",
      description: "Import all client data from the Google Sheets",
      icon: <FaDownload className="text-blue-500" />,
      color: "bg-blue-50 hover:bg-blue-100 border-blue-200",
      selectedColor: "bg-blue-100 border-blue-400",
    },

    {
      id: "upload-excel",
      title: "Upload Excel Sheet",
      description: "Upload your own Excel file with client data",
      icon: <FaUpload className="text-purple-500" />,
      color: "bg-purple-50 hover:bg-purple-100 border-purple-200",
      selectedColor: "bg-purple-100 border-purple-400",
    },
  ];

  const handleOptionSelect = (optionId) => {
    setSelectedOption(optionId);
    if (optionId !== "upload-excel") {
      setUploadData({});
    }
  };

  const handleImport = async () => {
    if (!selectedOption) {
      toast.error("Please select an import option");
      return;
    }

    try {
      switch (selectedOption) {
        case "import-all":
          if (onImportAll) {
            await onImportAll();
          }
          break;
        case "import-selected":
          if (onImportSelected) {
            await onImportSelected();
          } else if (onImportSelected) {
            // Fallback to selected import if updated import is not available
            await onImportSelected();
          }
          break;
        case "upload-excel":
          if (!uploadData.excelFile) {
            toast.error("Please upload an Excel file first");
            return;
          }
          await handleExcelUpload();
          break;
        default:
          toast.error("Invalid import option selected");
          return;
      }
      handleClose();
    } catch (error) {
      console.error("Import error:", error);
      toast.error("Error during import process");
    }
  };

  const handleExcelUpload = async () => {
    try {
      setIsUploading(true);
      // Here you would implement the Excel file processing logic
      // For now, we'll just show a success message
      toast.success("Excel file uploaded successfully");
      // You might want to call an API endpoint to process the uploaded Excel file
      // const response = await axios.post(`${API_URL}/clients/import-excel`, {
      //   fileUrl: uploadData.excelFile
      // });
    } catch (error) {
      console.error("Excel upload error:", error);
      toast.error("Error uploading Excel file");
    } finally {
      setIsUploading(false);
    }
  };

  const resetModal = () => {
    setSelectedOption("");
    setUploadData({});
    setIsUploading(false);
  };

  const handleModalClose = () => {
    resetModal();
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleModalClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        className: "rounded-lg",
      }}
    >
      <DialogTitle className="flex justify-between items-center border-b pb-3">
        <div>
          <h2 className="text-xl font-semibold text-app-text">Import Client Data</h2>
          <p className="text-sm text-app-text-secondary mt-1">
            Choose how you want to import client data
          </p>
        </div>
        <IconButton onClick={handleModalClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent className="py-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {options.map((option) => (
            <div
              key={option.id}
              className={`border-2 rounded-lg p-4 cursor-pointer transition-all duration-200 ${
                selectedOption === option.id ? option.selectedColor : option.color
              }`}
              onClick={() => handleOptionSelect(option.id)}
            >
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-10 h-10 rounded-full bg-app-surface flex items-center justify-center shadow-sm">
                    {option.icon}
                  </div>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      name="import-option"
                      checked={selectedOption === option.id}
                      onChange={() => handleOptionSelect(option.id)}
                      className="text-primary focus:ring-blue-500"
                    />
                    <h3 className="text-lg font-medium text-app-text">{option.title}</h3>
                  </div>
                  <p className="text-sm text-app-text-secondary mt-1">{option.description}</p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {selectedOption !== "upload-excel" && (
          <div>
            <InputField
              label="Google Sheet URL"
              placeholder="Enter Google Sheet URL"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              divClassName="mt-8"
            />
            <div className="mt-3 text-xs text-app-text-secondary">
              <p>
                <span className="font-bold">Note:</span> Please use the URL of the sheet with proper
                format you want to import or consult with your dev team to ensure proper format.
              </p>
            </div>
          </div>
        )}
        {/* Excel Upload Section */}
        {selectedOption === "upload-excel" && (
          <div className="mt-6 p-4 bg-app-surface-muted rounded-lg">
            <UploadFile
              data={uploadData}
              setData={setUploadData}
              imageKey="excelFile"
              label="Excel File"
              required={true}
              className="mb-0"
            />
            <div className="mt-3 text-xs text-app-text-secondary">
              <p>• Supported formats: .xlsx, .xls</p>
              <p>• Make sure your Excel file has the correct column headers</p>
              <p>• Maximum file size: {MAX_UPLOAD_FILE_SIZE_LABEL}</p>
            </div>
          </div>
        )}
      </DialogContent>

      <DialogActions className="border-t px-6 py-4">
        <Button onClick={handleModalClose} color="inherit" className="mr-2">
          Cancel
        </Button>
        <Button
          onClick={handleImport}
          variant="contained"
          disabled={!selectedOption || isLoading || isUploading}
          startIcon={
            isLoading || isUploading ? <CircularProgress size={16} color="inherit" /> : null
          }
          className="min-w-[120px]"
        >
          {isLoading || isUploading
            ? "Processing..."
            : selectedOption === "upload-excel"
            ? "Upload & Import"
            : "Import Data"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportModal;
